import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import AuthContext from '../contexts/AuthContext';
import { confirmDialog } from '../components/ConfirmDialog';

const containerStyle = {
  padding: '1.5rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: '#f8f9fa'
};

const cardStyle = {
  backgroundColor: '#fff',
  padding: '2.5rem',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  maxWidth: '600px',
  width: '90%',
  border: '1px solid #e9ecef',
  position: 'relative'
};

const buttonStyle = {
  backgroundColor: '#17a2b8',
  color: '#fff',
  border: 'none',
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(23, 162, 184, 0.3)',
  fontSize: '16px'
};

const buttonHoverStyle = {
  backgroundColor: '#138496',
  boxShadow: '0 6px 16px rgba(23, 162, 184, 0.4)',
  transform: 'scale(1.05)'
};

const headerStyle = {
  textAlign: 'center',
  marginBottom: '2rem'
};

const speedButtonStyle = {
  backgroundColor: '#6c757d',
  color: '#fff',
  border: 'none',
  padding: '0.75rem 1.25rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  fontSize: '16px',
  margin: '0.5rem',
  minWidth: '80px'
};

const speedButtonHoverStyle = {
  backgroundColor: '#5a6268',
  transform: 'scale(1.05)'
};

const speedButtonActiveStyle = {
  backgroundColor: '#17a2b8',
  boxShadow: '0 4px 12px rgba(23, 162, 184, 0.3)'
};

const infoBoxStyle = {
  backgroundColor: '#e8f4f8',
  border: '1px solid #bee5eb',
  borderRadius: '8px',
  padding: '1rem',
  margin: '1.5rem 0',
  fontSize: '14px',
  color: '#0c5460'
};

const TestSpeedSelection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [testName, setTestName] = useState('');
  const [testCategory, setTestCategory] = useState('');
  const [dictationWpm, setDictationWpm] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSpeed, setSelectedSpeed] = useState(0);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [source, setSource] = useState(''); // 'play' or 'listen-only'

  // Speed options from -15 to +50 in increments of 5
  const speedOptions = [-15, -10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

  // Clear any saved speed from localStorage when component loads
  useEffect(() => {
    localStorage.removeItem('dictationSpeed');
    setSelectedSpeed(0); // Reset to default speed
    
    // Read source from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');
    if (sourceParam) {
      setSource(sourceParam);
    }
  }, []);

  // Fetch test metadata
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const { data } = await api.get(`/tests/${id}`);
        
        if (data.testType === 'premium' && (!user || !user.isPremium)) {
          setError('This is a premium test. Please upgrade to a premium subscription to listen to the audio.');
          return;
        }

        setTestName(data.name);
        setTestCategory(data.category);
        setDictationWpm(data.dictationWpm || 0);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch test information');
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [id, user]);

  const handleSpeedSelect = (speed) => {
    setSelectedSpeed(speed);
    setShowSpeedOptions(false);
  };

  const handleConfirm = () => {
    // Save the selected speed
    console.log('Confirming speed selection:', selectedSpeed, source);
    localStorage.setItem('dictationSpeed', selectedSpeed.toString());
    
    // Navigate based on source
    if (source === 'play') {
      console.log('Selected speed saved:', selectedSpeed, id, 'navigating to typing page');
      navigate(`/tests/${id}`);
    } else {
      console.log('Selected speed saved:', selectedSpeed, id, 'navigating to listen-only page');
      navigate(`/listen-only/${id}`);
    }
  };

  const handleBack = () => {
    navigate('/tests');
  };

  const getEffectiveSpeed = () => {
    return dictationWpm + selectedSpeed;
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #17a2b8',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite',
          margin: '2rem auto'
        }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: '#fff5f5',
            border: '1px solid #fed7d7',
            borderRadius: '12px',
            color: '#e53e3e'
          }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Error</h3>
            <p>{error}</p>
            <button 
              onClick={handleBack}
              style={{
                ...buttonStyle,
                marginTop: '1rem',
                backgroundColor: '#6c757d'
              }}
            >
              Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h2 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Select Dictation Speed</h2>
          <p style={{ color: '#6c757d', fontSize: '16px' }}>
            Test: {testName}
          </p>
          <p style={{ color: '#6c757d', fontSize: '14px' }}>
            Category: {testCategory.toUpperCase()}
          </p>
        </div>

        <div style={infoBoxStyle}>
          <p style={{ margin: '0 0 0.5rem 0' }}>
            <strong>Base Dictation Speed:</strong> {dictationWpm} WPM
          </p>
          <p style={{ margin: '0' }}>
            {source === 'play' 
              ? 'Select a speed adjustment to change the playback speed during your typing test.'
              : 'Select a speed adjustment to change the playback speed of the audio.'
            }
          </p>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'inline-block', position: 'relative' }}>
            <button
              onClick={() => setShowSpeedOptions(!showSpeedOptions)}
              style={{
                ...buttonStyle,
                padding: '1rem 2rem',
                fontSize: '18px',
                backgroundColor: selectedSpeed !== 0 ? '#28a745' : '#17a2b8'
              }}
              onMouseEnter={(e) => Object.assign(e.target.style, buttonHoverStyle)}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = selectedSpeed !== 0 ? '#28a745' : '#17a2b8';
                e.target.style.boxShadow = '0 4px 12px rgba(23, 162, 184, 0.3)';
                e.target.style.transform = 'none';
              }}
            >
              {selectedSpeed !== 0 ? `+${selectedSpeed}%` : 'Select Speed'}
            </button>
            
            {showSpeedOptions && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginTop: '0.5rem',
                backgroundColor: '#fff',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '1rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                minWidth: '350px'
              }}>
                <div style={{ marginBottom: '0.75rem', fontWeight: '600', color: '#2c3e50', textAlign: 'center' }}>
                  Choose Speed Adjustment
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {speedOptions.map((speed) => (
                    <button
                      key={speed}
                      onClick={() => handleSpeedSelect(speed)}
                      style={{
                        ...speedButtonStyle,
                        backgroundColor: selectedSpeed === speed ? '#17a2b8' : '#6c757d',
                        ...(selectedSpeed === speed ? speedButtonActiveStyle : {}),
                        ...(selectedSpeed === speed ? {} : speedButtonHoverStyle)
                      }}
                      onMouseEnter={(e) => {
                        if (selectedSpeed !== speed) {
                          e.target.style.backgroundColor = '#5a6268';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedSpeed !== speed) {
                          e.target.style.backgroundColor = '#6c757d';
                        }
                      }}
                    >
                      {speed > 0 ? `+${speed}%` : `${speed}%`}
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '16px', color: '#2c3e50', fontWeight: '600' }}>
                  Effective Speed: <strong>{getEffectiveSpeed()} WPM</strong>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button
            onClick={handleBack}
            style={{
              ...buttonStyle,
              backgroundColor: '#6c757d'
            }}
            onMouseEnter={(e) => Object.assign(e.target.style, buttonHoverStyle)}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6c757d';
              e.target.style.boxShadow = '0 4px 12px rgba(108, 117, 125, 0.3)';
              e.target.style.transform = 'none';
            }}
          >
            ← Back to Tests
          </button>
          <button
            onClick={handleConfirm}
            disabled={isConfirming}
            style={{
              ...buttonStyle,
              backgroundColor: '#28a745',
              minWidth: '150px'
            }}
            onMouseEnter={(e) => Object.assign(e.target.style, buttonHoverStyle)}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#28a745';
              e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
              e.target.style.transform = 'none';
            }}
          >
            {isConfirming ? 'Confirming...' : (source === 'play' ? 'Start Test' : 'Start Listening')}
          </button>
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#f8f9fa',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#6c757d'
        }}>
          <p>{source === 'play' 
            ? 'Selected speed will be applied during your typing test.'
            : 'Selected speed will be applied to the audio playback.'
          }</p>
          <p>The speed setting will be remembered for future sessions.</p>
        </div>
      </div>
    </div>
  );
};

export default TestSpeedSelection;
